import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { Home } from "lucide-react";
import { money, CatBox, CatImage, CreateQuoteModal, OrangeBar, barSelect, barBtn } from "./catalogShared";

export default function GazeboSelector({ onExit, onNav }) {
  const [gazebos, setGazebos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [quote, setQuote] = useState(null);
  const [opts, setOpts] = useState({});

  useEffect(() => { api("GET", "/gazebos/catalogue").then((r) => setGazebos(r || [])).catch(() => setGazebos([])).finally(() => setLoading(false)); api("GET", "/catalogue/filter-options").then((r) => setOpts(r || {})).catch(() => setOpts({})); }, []);

  const models = useMemo(() => Array.from(new Set(gazebos.map((g) => g.model).filter(Boolean))).sort(), [gazebos]);

  const shown = useMemo(() => gazebos.filter((g) => {
    if (model && g.model !== model) return false;
    if (maxPrice) {
      const p = Number(g.erected_price || g.ready_to_go_price || 0);
      if (p > Number(maxPrice)) return false;
    }
    return true;
  }), [gazebos, model, maxPrice]);

  const goStock = () => onNav && onNav("tab:inventory");

  const PriceRow = ({ label, value, quoteName, testid }) => (
    <div className="grid grid-cols-[150px_120px_auto] items-center gap-2">
      <span className="text-[12px] font-semibold text-slate-800 text-right">{label}:</span>
      <span className="px-2 py-1 border border-[#b9b9b9] bg-white text-[13px] font-black text-[#123a63] text-center">{money(value)}</span>
      <button onClick={() => setQuote({ title: quoteName, item: { name: quoteName, price: value } })} data-testid={testid} className="rounded-sm bg-[#4a86d8] text-white font-black text-[12px] px-3 py-1.5 hover:bg-[#2a6cb5]">Create Quotation</button>
    </div>
  );

  return (
    <div data-testid="gazebo-search-panel" className="rounded-xl overflow-hidden bg-white border border-[#c3d0e6]">
      <div className="flex items-center gap-2 bg-[#1a4f8a] px-3 py-1.5">
        <Home className="h-4 w-4 text-white" /><span className="text-white font-black text-sm">Gazebo Search</span>
      </div>

      <OrangeBar>
        <label className="flex items-center gap-2 text-[12px] font-black text-white">Model
          <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="gazebo-f-model" className={barSelect}><option value="">All</option>{models.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </label>
        <label className="flex items-center gap-2 text-[12px] font-black text-white">Maximum Price
          <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} data-testid="gazebo-f-maxprice" className={barSelect}><option value="">All</option>{(opts.max_price || []).map((n) => <option key={n} value={n}>{`$${Number(n).toLocaleString("en-AU")}`}</option>)}</select>
        </label>
        <div className="ml-auto flex flex-col gap-1.5">
          <button onClick={goStock} data-testid="gazebo-view-stock" className={barBtn}>View Stock Gazebos</button>
          <button onClick={onExit} data-testid="gazebo-close-form" className={barBtn}>Close Form</button>
        </div>
      </OrangeBar>

      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2" data-testid="gazebo-result-count">{loading ? "Loading gazebos…" : `${shown.length} gazebo${shown.length === 1 ? "" : "s"}.`}</p>
        <div className="divide-y-2 divide-slate-200">
          {shown.map((g) => (
            <div key={g.id} data-testid="gazebo-row" className="py-3">
              <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2">
                <CatBox label="Width" value={g.width} labelW={56} />
                <CatBox label="Depth" value={g.depth} labelW={56} />
                <CatBox label="Height" value={g.height} labelW={56} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_1fr_200px] gap-3 items-start">
                <div>
                  <CatBox label="Model" value={g.model} />
                  <CatBox label="Brand" value={g.brand} />
                  <CatBox label="Persons" value={g.construction} />
                  <CatBox label="Cabinet" value={g.shape} />
                  <CatBox label="Style" value={g.style} />
                </div>
                <div>
                  {[0, 1, 2, 3, 4].map((i) => <CatBox key={i} label={`Inclusion ${i + 1}`} value={g.inclusions?.[i]} labelW={92} />)}
                </div>
                <div className="pt-1 space-y-1.5">
                  {g.diy_price > 0 && <PriceRow label="DIY Price" value={g.diy_price} quoteName={`Gazebo ${g.model} — DIY`} testid={`gazebo-quote-diy-${g.id}`} />}
                  <PriceRow label="Ready To Go Price" value={g.ready_to_go_price} quoteName={`Gazebo ${g.model} — Ready To Go`} testid={`gazebo-quote-rtg-${g.id}`} />
                  <PriceRow label="Erected Price" value={g.erected_price} quoteName={`Gazebo ${g.model} — Erected`} testid={`gazebo-quote-erected-${g.id}`} />
                </div>
                <CatImage url={g.image_url} alt={g.model} className="aspect-[4/3] w-full border border-[#9db8d6] rounded-sm" />
              </div>
            </div>
          ))}
          {!loading && shown.length === 0 && <p className="text-center text-slate-500 text-sm py-8">No gazebos match your filters.</p>}
        </div>
      </div>

      {quote && <CreateQuoteModal title={quote.title} item={quote.item} onClose={() => setQuote(null)} />}
    </div>
  );
}
